(() => {
  'use strict';

  if (!customElements.get('variant-selects')) {
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.requestId = 0;
        this.addEventListener('change', this.onVariantChange.bind(this));
      }

      connectedCallback() {
        this.hidden = false;
        const fallback = document.getElementById(`VariantFallback-${this.dataset.section}`);
        if (fallback) fallback.hidden = true;
        this.options = this.getSelectedOptions();
        this.updateOptionAvailability();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.requestId += 1;
      }

      onVariantChange(event) {
        if (!event.target.matches('input[type="radio"], select')) return;
        this.abortController?.abort();
        const requestId = ++this.requestId;
        this.updateSelectedLabel(event.target);
        this.options = this.getSelectedOptions();
        this.currentVariant = this.getVariantData().find((variant) =>
          variant.options.every((option, index) => option === this.options[index])
        );
        this.updateOptionAvailability();
        this.setPending();

        if (!this.currentVariant) {
          this.setUnavailable();
          return;
        }

        this.updateUrl();
        this.updateMedia();
        this.renderProductInfo(this.currentVariant, requestId);
        this.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: this.currentVariant }
        }));
      }

      updateSelectedLabel(input) {
        const label = input.closest('fieldset')?.querySelector('[data-selected-option]');
        if (label) label.textContent = input.dataset.displayLabel || input.value;
      }

      getSelectedOptions() {
        return Array.from(this.querySelectorAll('fieldset')).map((fieldset) =>
          fieldset.querySelector('input[type="radio"]:checked, select')?.value
        );
      }

      getVariantData() {
        if (this.variantData) return this.variantData;
        try {
          this.variantData = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
        } catch (error) {
          this.variantData = [];
        }
        return this.variantData;
      }

      updateOptionAvailability() {
        this.querySelectorAll('fieldset').forEach((fieldset, position) => {
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const available = this.getVariantData().some((variant) => variant.available &&
              variant.options[position] === input.value &&
              variant.options.slice(0, position).every((value, index) => value === this.options[index]));
            input.toggleAttribute('data-option-unavailable', !available);
            const label = input.nextElementSibling;
            label?.classList.toggle('variant-picker__label--unavailable', !available);
            const hint = label?.querySelector('[data-option-availability]');
            if (hint) hint.textContent = available ? '' : this.dataset.optionUnavailable;
          });
        });
      }

      getProductSection() {
        return this.closest('[data-product-url]') || document;
      }

      getVariantUrl(variant) {
        const url = new URL(this.dataset.url || window.location.pathname, window.location.origin);
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.forEach((value, key) => {
          if (key !== 'variant' && key !== 'section_id') url.searchParams.set(key, value);
        });
        url.searchParams.set('variant', variant.id);
        url.hash = currentUrl.hash;
        return url;
      }

      updateUrl() {
        if (!this.dataset.url || window.Shopify?.designMode) return;
        const url = this.getVariantUrl(this.currentVariant);
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      }

      updateMedia() {
        const mediaId = this.currentVariant.featured_media?.id;
        if (!mediaId) return;
        const gallery = this.getProductSection().querySelector('.product-media__list');
        const galleryElement = gallery?.closest('wpw-gallery');
        if (typeof galleryElement?.show === 'function') {
          galleryElement.show(mediaId);
          return;
        }
        const media = gallery?.querySelector(`[data-media-id$="-${mediaId}"]`);
        if (!media) return;
        gallery.querySelectorAll('.product-media__item--featured').forEach((item) => {
          item.classList.remove('product-media__item--featured');
        });
        media.classList.add('product-media__item--featured');
        gallery.prepend(media);
      }

      toggleAddButton(disable, text) {
        this.getProductSection().querySelectorAll('[data-add-to-cart]').forEach((button) => {
          button.disabled = disable;
          const label = button.querySelector('[data-add-to-cart-text]');
          if (label && text) label.textContent = text;
        });
      }

      setPending() {
        const section = this.getProductSection();
        section.dataset.variantPending = 'true';
        section.querySelectorAll('[data-variant-id]').forEach((input) => { input.disabled = true; });
        section.querySelectorAll('[data-product-form-error]').forEach((error) => { error.hidden = true; });
        section.querySelectorAll('[data-buy-buttons], [data-product-price]').forEach((element) => {
          element.setAttribute('aria-busy', 'true');
        });
        section.querySelectorAll('.product-form__dynamic-checkout').forEach((element) => { element.hidden = true; });
        this.toggleAddButton(true, this.dataset.loading);
      }

      setUnavailable() {
        const section = this.getProductSection();
        section.querySelectorAll('[data-product-price], [data-product-inventory], [data-product-sku]').forEach((element) => {
          element.hidden = true;
          element.removeAttribute('aria-busy');
        });
        section.querySelectorAll('[data-buy-buttons]').forEach((element) => { element.removeAttribute('aria-busy'); });
        this.toggleAddButton(true, this.dataset.unavailable);
      }

      async renderProductInfo(variant, requestId) {
        const variantUrl = this.getVariantUrl(variant);
        if (!this.dataset.section) {
          window.location.assign(variantUrl.toString());
          return;
        }
        const controller = new AbortController();
        this.abortController = controller;
        const url = new URL(variantUrl);
        url.searchParams.set('section_id', this.dataset.section);

        try {
          const response = await fetch(url.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Variant request failed');
          const html = await response.text();
          if (controller.signal.aborted || requestId !== this.requestId || !this.isConnected) return;
          const source = new DOMParser().parseFromString(html, 'text/html');
          const sourceSection = source.querySelector(`[data-section-id="${this.dataset.section}"]`);
          const sourceVariant = sourceSection?.querySelector('[data-variant-id]');
          if (!sourceVariant || sourceVariant.value !== String(variant.id)) throw new Error('Variant response mismatch');
          const section = this.getProductSection();
          const quantity = section.querySelector('.product__quantity');
          const nextQuantity = sourceSection.querySelector('.product__quantity');
          if (quantity && nextQuantity) {
            const oldInput = quantity.querySelector('[name="quantity"]');
            const newInput = nextQuantity.querySelector('[name="quantity"]');
            if (oldInput && newInput) {
              const minimum = Number(newInput.min || 1);
              const maximum = newInput.max ? Number(newInput.max) : Infinity;
              const increment = Number(newInput.step || 1);
              const requested = Number(oldInput.value);
              const bounded = Math.min(maximum, Math.max(minimum, Number.isFinite(requested) ? requested : minimum));
              newInput.value = minimum + Math.floor((bounded - minimum) / increment) * increment;
            }
            quantity.replaceWith(nextQuantity);
          }
          ['[data-product-price]', '[data-product-sku]', '[data-product-inventory]', '[data-buy-buttons]'].forEach((selector) => {
            const destination = section.querySelector(selector);
            const replacement = sourceSection.querySelector(selector);
            if (destination && replacement) destination.replaceWith(replacement);
          });
          delete section.dataset.variantPending;
          section.querySelectorAll('[data-variant-id]').forEach((input) => {
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          window.Shopify?.PaymentButton?.init();
        } catch (error) {
          if (controller.signal.aborted || requestId !== this.requestId || !this.isConnected) return;
          window.location.assign(variantUrl.toString());
        }
      }
    }

    customElements.define('variant-selects', VariantSelects);
  }

  if (!customElements.get('product-form')) {
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-add-to-cart]');
        this.errorWrapper = this.querySelector('[data-product-form-error]');
        this.errorMessage = this.errorWrapper?.querySelector('.product-form__error-message');
        this.form?.addEventListener('submit', this.onSubmit);
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        window.removeEventListener('pageshow', this.onPageShow);
      }

      onPageShow(event) {
        if (event.persisted && this.loading) this.setLoading(false);
      }

      async onSubmit(event) {
        event.preventDefault();
        const section = this.closest('[data-product-url]');
        const variantInput = this.form?.querySelector('[data-variant-id]');
        if (!this.form || !this.submitButton || this.submitButton.disabled || this.loading ||
          section?.dataset.variantPending || !variantInput?.value || variantInput.disabled) return;
        if (!this.form.reportValidity()) return;
        const body = new FormData(this.form);
        this.setLoading(true);
        this.showError();

        try {
          const endpoint = new URL(this.form.action);
          if (!endpoint.pathname.endsWith('.js')) endpoint.pathname += '.js';
          const response = await fetch(endpoint.toString(), {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body
          });
          const result = await response.json();
          if (!response.ok || result.status >= 400) {
            throw new Error(result.description || result.message || this.dataset.errorMessage);
          }
          window.location.assign(this.dataset.cartUrl || '/cart');
        } catch (error) {
          this.showError(error.message || this.dataset.errorMessage);
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        this.loading = loading;
        this.submitButton.disabled = loading;
        this.submitButton.classList.toggle('is-loading', loading);
        this.submitButton.setAttribute('aria-busy', String(loading));
        const spinner = this.submitButton.querySelector('.button__spinner');
        if (spinner) spinner.hidden = !loading;
        const section = this.closest('[data-product-url]');
        if (loading) {
          this.lockedControls = Array.from(section?.querySelectorAll('variant-selects input, variant-selects select, .product__quantity input, .product__quantity button') || [])
            .filter((control) => !control.disabled);
          this.lockedControls.forEach((control) => { control.disabled = true; });
        } else {
          this.lockedControls?.forEach((control) => { control.disabled = false; });
        }
        this.querySelectorAll('.product-form__dynamic-checkout').forEach((element) => { element.hidden = loading; });
      }

      showError(message = '') {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorWrapper.hidden = !message;
        this.errorMessage.textContent = message;
      }
    }

    customElements.define('product-form', ProductForm);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (!button || button.disabled || button.closest('cart-items')) return;
    const quantity = button.parentElement?.querySelector('.quantity__input');
    if (!quantity || quantity.disabled) return;
    event.preventDefault();
    if (button.matches('[data-quantity-plus]')) quantity.stepUp();
    else quantity.stepDown();
    quantity.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();
